import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { classNames } from "@/lib/format";
import {
  DEFAULT_KUNDE_RECHNUNGS_EINSTELLUNGEN,
  updateKundeRechnungsEinstellungen,
  type KundeMailKanal,
  type KundeRechnungsEinstellungen,
  type Rechnungsformat,
} from "@/lib/kontakte";

interface KundeRechnungsEinstellungenSectionProps {
  geschaeftspartnerId: number;
  einstellungen: KundeRechnungsEinstellungen | null;
  /** Stamm-E-Mail des Kunden – wird in leere Kanäle vorausgefüllt. */
  defaultEmail?: string | null;
  onUpdated: (einstellungen: KundeRechnungsEinstellungen) => void;
}

type MailKanalKey =
  | "rechnung"
  | "zahlungserinnerung"
  | "mahnung_1"
  | "mahnung_2"
  | "mahnung_3";

const MAIL_KANAL_LABELS: Record<MailKanalKey, string> = {
  rechnung: "Rechnung",
  zahlungserinnerung: "Zahlungserinnerung",
  mahnung_1: "1. Mahnung",
  mahnung_2: "2. Mahnung",
  mahnung_3: "3. Mahnung",
};

function withDefaultEmail(
  kanal: KundeMailKanal,
  defaultEmail: string | null | undefined,
): KundeMailKanal {
  const email = kanal.email?.trim() || defaultEmail?.trim() || null;
  return {
    email,
    cc_projektansprechpartner: kanal.cc_projektansprechpartner,
  };
}

function toFormState(
  einstellungen: KundeRechnungsEinstellungen | null,
  defaultEmail?: string | null,
): KundeRechnungsEinstellungen {
  const fallback = defaultEmail?.trim() || null;
  if (!einstellungen) {
    const empty = DEFAULT_KUNDE_RECHNUNGS_EINSTELLUNGEN;
    return {
      rechnungsformat: empty.rechnungsformat,
      leitweg_id: empty.leitweg_id,
      ust_id: empty.ust_id,
      reverse_charge_13b: empty.reverse_charge_13b,
      stundennachweis_mit_rechnung: empty.stundennachweis_mit_rechnung,
      rechnung: withDefaultEmail(empty.rechnung, fallback),
      zahlungserinnerung: withDefaultEmail(empty.zahlungserinnerung, fallback),
      mahnung_1: withDefaultEmail(empty.mahnung_1, fallback),
      mahnung_2: withDefaultEmail(empty.mahnung_2, fallback),
      mahnung_3: withDefaultEmail(empty.mahnung_3, fallback),
    };
  }
  return {
    rechnungsformat: einstellungen.rechnungsformat,
    leitweg_id: einstellungen.leitweg_id?.trim() || null,
    ust_id: einstellungen.ust_id?.trim() || null,
    reverse_charge_13b: Boolean(einstellungen.reverse_charge_13b),
    stundennachweis_mit_rechnung: einstellungen.stundennachweis_mit_rechnung !== false,
    rechnung: withDefaultEmail(einstellungen.rechnung, fallback),
    zahlungserinnerung: withDefaultEmail(einstellungen.zahlungserinnerung, fallback),
    mahnung_1: withDefaultEmail(einstellungen.mahnung_1, fallback),
    mahnung_2: withDefaultEmail(einstellungen.mahnung_2, fallback),
    mahnung_3: withDefaultEmail(einstellungen.mahnung_3, fallback),
  };
}

function mailKanalToPayload(kanal: KundeMailKanal): KundeMailKanal {
  const email = kanal.email?.trim() || null;
  return {
    email,
    cc_projektansprechpartner: kanal.cc_projektansprechpartner,
  };
}

function MailKanalRow({
  label,
  kanal,
  onChange,
}: {
  label: string;
  kanal: KundeMailKanal;
  onChange: (next: KundeMailKanal) => void;
}) {
  return (
    <div className="rounded-ios border border-separator/70 p-4">
      <h4 className="mb-3 text-sm font-medium text-label">{label}</h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="E-Mail-Adresse"
          type="email"
          value={kanal.email || ""}
          onChange={(e) => onChange({ ...kanal, email: e.target.value })}
          placeholder="rechnung@kunde.de"
        />
        <label className="flex cursor-pointer items-center gap-3 self-end pb-2">
          <input
            type="checkbox"
            checked={kanal.cc_projektansprechpartner}
            onChange={(e) =>
              onChange({ ...kanal, cc_projektansprechpartner: e.target.checked })
            }
            className="h-4 w-4 rounded border-separator"
          />
          <span className="text-sm text-label">
            Projektansprechpartner in CC
          </span>
        </label>
      </div>
    </div>
  );
}

export function KundeRechnungsEinstellungenSection({
  geschaeftspartnerId,
  einstellungen,
  defaultEmail,
  onUpdated,
}: KundeRechnungsEinstellungenSectionProps) {
  const baseline = useMemo(
    () => toFormState(einstellungen, defaultEmail),
    [einstellungen, defaultEmail],
  );
  const [form, setForm] = useState<KundeRechnungsEinstellungen>(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  useEffect(() => {
    if (!dirty) {
      setForm(baseline);
    }
  }, [baseline, dirty]);

  function setMailKanal(key: MailKanalKey, next: KundeMailKanal) {
    setForm((f) => ({ ...f, [key]: next }));
  }

  async function handleSave() {
    const payload: KundeRechnungsEinstellungen = {
      rechnungsformat: form.rechnungsformat,
      leitweg_id: form.leitweg_id?.trim() || null,
      ust_id: form.ust_id?.trim() || null,
      reverse_charge_13b: form.reverse_charge_13b,
      stundennachweis_mit_rechnung: form.stundennachweis_mit_rechnung,
      rechnung: mailKanalToPayload(form.rechnung),
      zahlungserinnerung: mailKanalToPayload(form.zahlungserinnerung),
      mahnung_1: mailKanalToPayload(form.mahnung_1),
      mahnung_2: mailKanalToPayload(form.mahnung_2),
      mahnung_3: mailKanalToPayload(form.mahnung_3),
    };
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateKundeRechnungsEinstellungen(geschaeftspartnerId, payload);
      const saved = updated.rechnungs_einstellungen ?? payload;
      onUpdated(saved);
      setSuccess("Rechnungseinstellungen gespeichert.");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Rechnungseinstellungen konnten nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Rechnung & Mahnwesen"
      description="Format, Steuerangaben und Empfänger je Versandkanal. Leere Felder werden mit der zentralen Kunden-E-Mail vorausgefüllt; im Mahnwesen gilt weiterhin der Fallback auf die Rechnungs-E-Mail."
      actions={
        <Button onClick={() => void handleSave()} disabled={saving || !dirty}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert variant="success" title="Gespeichert" className="mb-4">
          {success}
        </Alert>
      ) : null}

      <div className="space-y-6">
        <fieldset>
          <legend className="mb-3 text-sm font-medium text-label">Rechnungsformat</legend>
          <div className="flex flex-wrap gap-4">
            {(
              [
                { value: "zugpferd", label: "ZUGFeRD-Rechnung" },
                { value: "xrechnung", label: "X-Rechnung" },
              ] as const
            ).map((option) => (
              <label
                key={option.value}
                className={classNames(
                  "flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm text-label transition",
                  form.rechnungsformat === option.value
                    ? "border-accent bg-accent-soft"
                    : "border-separator hover:bg-fill-quaternary",
                )}
              >
                <input
                  type="radio"
                  name="rechnungsformat"
                  value={option.value}
                  checked={form.rechnungsformat === option.value}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      rechnungsformat: option.value as Rechnungsformat,
                    }))
                  }
                  className="h-4 w-4"
                />
                {option.label}
                {option.value === "zugpferd" ? (
                  <span className="text-xs text-label-secondary">(Standard)</span>
                ) : null}
              </label>
            ))}
          </div>
          {form.rechnungsformat === "zugpferd" ? (
            <label className="mt-4 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.stundennachweis_mit_rechnung}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    stundennachweis_mit_rechnung: e.target.checked,
                  }))
                }
                className="mt-0.5 h-4 w-4 rounded border-separator"
              />
              <span className="text-sm text-label">
                <span className="font-medium">
                  Stundennachweis standardmäßig mit der Rechnung mitsenden
                </span>
                <span className="block text-xs text-label-secondary">
                  Beim E-Mail-Versand werden verknüpfte Stundennachweis-PDFs vorausgewählt. Abwählen,
                  wenn der Nachweis nicht zusätzlich als Anhang mitgeschickt werden soll.
                </span>
              </span>
            </label>
          ) : null}
        </fieldset>

        <TextInput
          label="Leitweg-ID"
          value={form.leitweg_id || ""}
          onChange={(e) =>
            setForm((f) => ({ ...f, leitweg_id: e.target.value || null }))
          }
          placeholder="z. B. 991-12345-67"
          hint="Vorschlag für neue Aufträge und Rechnungen. Vom Rechnungsempfänger vorgegebene Kennung für die E-Rechnung. Wird als Käuferreferenz (BT-10) übertragen, ohne eine am Auftrag eingetragene Kennung zu überschreiben."
        />

        <div className="rounded-ios border border-separator/70 p-4">
          <h4 className="mb-3 text-sm font-medium text-label">Umsatzsteuer</h4>
          <div className="space-y-4">
            <TextInput
              label="USt-IdNr. des Kunden"
              value={form.ust_id || ""}
              onChange={(e) => setForm((f) => ({ ...f, ust_id: e.target.value || null }))}
              placeholder="DE123456789"
              hint="Landet als Käufer-USt-IdNr. (BT-48) in der E-Rechnung – bei § 13b Pflichtangabe."
            />
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={form.reverse_charge_13b}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reverse_charge_13b: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 rounded border-separator"
              />
              <span className="text-sm text-label">
                <span className="font-medium">
                  § 13b UStG – Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)
                </span>
                <span className="block text-xs text-label-secondary">
                  Neue Rechnungen für diesen Kunden werden ohne Umsatzsteuer (0 %) erstellt und
                  erhalten den Pflichthinweis auf PDF und E-Rechnung. Erfordert die USt-IdNr. des
                  Kunden.
                </span>
              </span>
            </label>
            {form.reverse_charge_13b && !(form.ust_id || "").trim() ? (
              <Alert variant="warning" title="USt-IdNr. fehlt">
                Für § 13b-Rechnungen verlangt die EN 16931 die USt-IdNr. des Leistungsempfängers
                (BT-48). Bitte oben eintragen, sonst lehnen Empfänger-Systeme die E-Rechnung ab.
              </Alert>
            ) : null}
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-label">Empfänger je Versandkanal</h4>
          {(Object.keys(MAIL_KANAL_LABELS) as MailKanalKey[]).map((key) => (
            <MailKanalRow
              key={key}
              label={MAIL_KANAL_LABELS[key]}
              kanal={form[key]}
              onChange={(next) => setMailKanal(key, next)}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
